import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify"; // Ensure you have react-toastify installed and configured

const BaseUrl = process.env.REACT_APP_API_URL;

// Axios request interceptor to append X-Session-ID header
axios.interceptors.request.use((config) => {
  const loginDataStr = Cookies.get("loginData");
  if (loginDataStr) {
    try {
      const loginData = JSON.parse(loginDataStr);
      const sessionId = loginData?.sessionId;
      if (sessionId) {
        config.headers["X-Session-ID"] = sessionId;
      }
    } catch (e) {
      // ignore
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Axios response interceptor to handle 401 Unauthorized (session revoked)
axios.interceptors.response.use((response) => {
  return response;
}, (error) => {
  if (error.response && error.response.status === 401) {
    Cookies.remove("loginData");
    window.location.href = "/";
    toast.error("Your session has been terminated or revoked.", {
      position: "top-right",
      autoClose: 3000,
    });
  }
  return Promise.reject(error);
});

// Define an async thunk for the login action
export const loginUser = createAsyncThunk(
  "auth/loginUser", // Action type prefix
  async ({ Email, Password }, { rejectWithValue }) => {
    try {
      const customDeviceName = localStorage.getItem("custom_device_name") || "";
      const response = await axios.post(
        `${BaseUrl}/CustomerSupport/sdfghjklgfdsartfyuiopytr`,
        {
          Password: Password,
          Email: Email,
          DeviceName: customDeviceName
        }
      );

      if (
        response.data.status === "Login as Owner" ||
        response.data.status === "Login as InHouse" ||
        response.data.status === "customer support"
      ) {
        const userProfile = response.data?.data?.Profile || response.data?.data?.profile;
        const userRole = response.data?.data?.Role?.toLowerCase();
        if (userRole !== "owner" && (userProfile === "Off" || userProfile?.toLowerCase() === "off")) {
          const errorMsg = "Your profile is inactive. Please contact administrator.";
          toast.error(errorMsg, {
            position: "top-right",
            autoClose: 3000,
          });
          return rejectWithValue(errorMsg);
        }

        Cookies.set("loginData", JSON.stringify(response.data), { expires: 7 });
        if (response.data?.sessionId) {
          localStorage.setItem("active_session_id", response.data.sessionId);
          Cookies.set("active_session_id", response.data.sessionId, { expires: 7 });
        }
        toast.success("Login successful!", {
          position: "top-right",
          autoClose: 3000,
        });
        return response.data; // Return the data to be stored in Redux state
      } else {
        // If login status indicates an error (e.g., incorrect credentials)
        const errorMsg = response?.data?.message || response?.data?.status || "Login failed";
        toast.error(`${errorMsg}`, {
          position: "top-right",
          autoClose: 3000,
        });
        // Reject with value will carry the response status to the rejected case
        return rejectWithValue(errorMsg);
      }
    } catch (error) {
      // Handle network errors or other exceptions during the API call
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.status ||
        error?.message ||
        "An error occurred during login. Please try again.";
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 3000,
      });
      // Reject with value will carry the error object to the rejected case for detailed error handling
      return rejectWithValue(errorMsg);
    }
  }
);

// LOGOUT THUNK (with API call)
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl}/Logout/LogoutApi/${id}`);

      // Check backend response (optional)
      if (response.data?.message === "Success") {
        Cookies.set("loginData", JSON.stringify(response.data), { expires: 7 });
        toast.info("Logged out successfully.", {
          position: "top-right",
          autoClose: 3000,
        });
        // Remove cookie after successful logout
        Cookies.remove("loginData");
        Cookies.remove("active_session_id");
        localStorage.removeItem("active_session_id");
        return true;
      } else {
        toast.warn("Logout failed. Try again.", {
          position: "top-right",
          autoClose: 3000,
        });
        return rejectWithValue(response.data);
      }
    } catch (error) {
      // toast.error("Error during logout. Please try again.", {
      //   position: "top-right",
      //   autoClose: 3000,
      // });
      // alert("Please Complete Your Daily Work.");
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

export const User = createAsyncThunk(
  "CustomerSupport/DynamicInHouse_findid",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/CustomerSupport/DynamicInHouse_findid/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// Create the auth slice
const authSlice = createSlice({
  name: "auth",
  initialState: {
    LoginData: null, // Stores the user's login data
    isdata: null,
    loading: false, // Indicates if an async operation (like login) is in progress
    error: null, // Stores any error messages
  },
  reducers: {
    // Synchronous reducer to initialize login data from cookies on app load
    initializeLoginData: (state) => {
      const storedLoginData = Cookies.get("loginData");
      if (storedLoginData) {
        try {
          const parsed = JSON.parse(storedLoginData);
          const userProfile = parsed?.data?.Profile || parsed?.data?.profile;
          const userRole = parsed?.data?.Role?.toLowerCase();
          if (userRole !== "owner" && (userProfile === "Off" || userProfile?.toLowerCase() === "off")) {
            Cookies.remove("loginData");
            Cookies.remove("active_session_id");
            localStorage.removeItem("active_session_id");
            state.LoginData = null;
            return;
          }
          state.LoginData = parsed;
        } catch (e) {
          state.LoginData = null;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle the pending state of the loginUser thunk
      .addCase(loginUser.pending, (state) => {
        state.loading = true; // Set loading to true
        state.error = null; // Clear any previous errors
      })
      // Handle the fulfilled (successful) state of the loginUser thunk
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false; // Set loading to false
        state.LoginData = action.payload; // Store the received login data
        state.error = null; // Clear any errors
      })
      // Handle the rejected (failed) state of the loginUser thunk
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false; // Set loading to false
        state.LoginData = null; // Clear login data on failure
        // Use the payload (error message from rejectWithValue) or a generic message
        state.error = action.payload || "Login failed";
      })

      // LOGOUT
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.LoginData = null;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Logout failed";
      })

      .addCase(User.pending, (state) => {
        state.loading = true;
      })
      .addCase(User.fulfilled, (state, action) => {
        state.loading = false;
        state.isdata = action.payload;
        state.error = null;
      })
      .addCase(User.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Logout failed";
      });
  },
});

// Export the synchronous actions
export const { logout, initializeLoginData } = authSlice.actions;
// Export the reducer as default
export default authSlice.reducer;
